import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import type { CreateDemoAccountResponse } from '../../types/demoAccount';
import { ToastService } from '../../services/toastService';

interface CredentialsSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  result: CreateDemoAccountResponse | null;
}

function copyToClipboard(text: string, label: string) {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      ToastService.success(`${label} copied to clipboard`, 'Copied');
    })
    .catch(() => {
      ToastService.error(
        'Failed to copy to clipboard. Please copy the text manually.',
        'Copy failed'
      );
    });
}

export const CredentialsSummaryDialog: React.FC<CredentialsSummaryDialogProps> = ({
  open,
  onClose,
  result,
}) => {
  if (!result) return null;

  const { demoAccount, credentials } = result;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Demo Account Created</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Alert severity="success">
            Successfully created 1 user, 1 entity, and {demoAccount.vesselCount} vessel(s) with
            voyages and compliance data.
          </Alert>

          <Typography variant="subtitle2" color="text.secondary">
            Share these credentials with the prospect:
          </Typography>

          <TextField
            label="Email"
            value={credentials.email}
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy email">
                      <IconButton
                        onClick={() => copyToClipboard(credentials.email, 'Email')}
                        edge="end"
                        size="small"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />

          <TextField
            label="Password"
            value={credentials.password}
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy password">
                      <IconButton
                        onClick={() => copyToClipboard(credentials.password, 'Password')}
                        edge="end"
                        size="small"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Typography variant="body2" color="text.secondary">
            Account: {demoAccount.entityName} ({demoAccount.mohaAccountNumber})
          </Typography>
          {demoAccount.demoExpiresAt && (
            <Typography variant="body2" color="text.secondary">
              Expires: {new Date(demoAccount.demoExpiresAt).toLocaleDateString()}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            const text = `Email: ${credentials.email}\nPassword: ${credentials.password}`;
            copyToClipboard(text, 'Credentials');
          }}
          variant="outlined"
        >
          Copy All
        </Button>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
